import { h, Host } from "@stencil/core";
import { getUniqueId } from "../../utils/unique-id";
import { hasSlot } from "../../utils/hasSlot";
/**
 * @slot (default) - Content of the label.
 * @slot "hint" - Content of the hint block.
 * @slot "buttons" - Buttons can be placed here.
 * @slot "other" - Additional content is placed at the bottom of the component.
 */
export class InputField {
    constructor() {
        this.label = "";
        this.value = "";
        this.inputId = "";
        this.type = "text";
        this.name = undefined;
        this.labelInfo = "";
        this.placeholder = "";
        this.disabled = false;
        this.required = false;
        this.error = false;
        this.errorMsg = "";
        this.errorIcon = "";
        this.hintText = "";
        this.clearable = false;
        this.unit = null;
        this.icon = null;
        this.inputmode = undefined;
        this.autocomplete = "off";
        this.copyToClipboard = false;
        this.hasFocus = false;
    }
    inputElement;
    domNode;
    /** Set inputId if it is unset */
    setInputId() {
        this.inputId = this.inputId || getUniqueId();
    }
    /** Returns the native input element */
    async getInputElement() {
        return this.inputElement;
    }
    /** Set the focus on the input element */
    async setFocus() {
        this.inputElement.focus();
    }
    /** Emitted when a keyboard input occurred. */
    uxInput;
    /** Emitted when the value has changed. */
    uxChange;
    /** Emitted when the input loses focus. */
    uxBlur;
    /** Emitted when the input has focus. */
    uxFocus;
    /** Emitted when the input is invalid. */
    uxInvalid;
    componentWillLoad() {
        this.setInputId();
    }
    /**
     * inputChange Event
     */
    inputChange;
    // --------------------------------
    // Event handlers
    // --------------------------------
    handleInput = (e) => {
        this.value = this.inputElement.value;
        this.uxInput.emit();
        this.inputChange.emit({
            currentValue: e.target.value,
            inputName: this.name,
            inputId: this.inputId
        });
    };
    handleChange = () => {
        this.value = this.inputElement.value;
        this.uxChange.emit();
    };
    handleFocus = () => {
        this.hasFocus = true;
        this.uxFocus.emit();
    };
    handleBlur = (event) => {
        // only emit blur if the whole component loses focus
        if (!this.domNode.contains(event.relatedTarget)) {
            this.hasFocus = false;
            this.uxBlur.emit();
        }
    };
    handleInvalid = () => {
        this.error = true;
        this.uxInvalid.emit();
    };
    handleClear = (event) => {
        event.stopPropagation();
        event.preventDefault();
        this.setFocus();
        this.value = "";
        this.uxChange.emit();
    };
    render() {
        const { label, labelInfo, value, inputId, type, name, placeholder, disabled, required, error, errorMsg, errorIcon, hintText, inputmode, clearable, unit, handleBlur, handleChange, handleClear, handleFocus, handleInput, handleInvalid, hasFocus, autocomplete, copyToClipboard } = this;
        const baseClass = "ux-input-field";
        const hasValue = value !== "";
        const haslabelSlot = hasSlot(this.domNode, "label");
        const hasIconSlot = hasSlot(this.domNode, "icon");
        const hasHintSlot = hasSlot(this.domNode, "hint");
        const hasButtons = hasSlot(this.domNode, "buttons");
        const hasLabelText = label && label.length > 0;
        const hasLabel = haslabelSlot || hasLabelText;
        const hasUnit = unit !== null;
        const hasUnitText = hasUnit && unit !== "";
        const hasHintText = hintText && hintText.length > 0;
        const hasHint = hasHintText || hasHintSlot;
        const iconName = error && errorIcon !== "" ? errorIcon : this.icon;
        const hasIconName = iconName && iconName.length > 0;
        const hasIcon = hasIconSlot || hasIconName;
        const isClearable = clearable && !disabled && hasValue;
        const labelId = `${baseClass}-label-${inputId}`;
        const hintId = `${baseClass}--hint-${inputId}`;
        return (h(Host, { key: 'fabc425a50f236aa9e56837b599ff8d55fc3e36a', class: {
                [baseClass]: true,
                [`${baseClass}--disabled`]: disabled,
                [`${baseClass}--clearable`]: isClearable,
                [`${baseClass}--numeric`]: hasUnit,
                [`${baseClass}--unit`]: hasUnitText,
                [`${baseClass}--error`]: error,
                [`${baseClass}--focus`]: hasFocus
            } }, h("ux-input-label", { key: 'b4f1c0f7abfced33c32d24b3876331a045f48b8a', text: label, labelId: labelId, labelClasses: `${baseClass}__label`, inputId: inputId, error: error, errorMsg: errorMsg, required: required, labelInfo: labelInfo }, h("slot", { key: 'e1fed7824c40efe2e80a91317014b6a5945898a0' })), h("div", { key: '735292f2c01688380e2153efab553a5df5bdaee8', class: `${baseClass}__field ux-u-background-white` }, h("div", { key: '3cc29e8105cd745d5413f62cc91e318d3d28896f', class: `${baseClass}__controls` }, h("input", { key: '11659ca2ae0e68727cb9808e89d190aaeb25b5fc', class: {
                [`ux-input`]: true,
                [`${baseClass}__input`]: true
            }, type: type, id: inputId, name: name, value: value, placeholder: placeholder, disabled: disabled, required: required, spellcheck: false, inputMode: inputmode, "aria-labelledby": hasLabel ? labelId : null, "aria-describedby": hasHint ? hintId : null,
            // @ts-expect-error Check for typing
            ref: (el) => (this.inputElement = el), onInput: handleInput, onChange: handleChange, onFocus: handleFocus, onBlur: handleBlur, onInvalid: handleInvalid, "aria-invalid": error ? "true" : "false", autocomplete: autocomplete }), isClearable && (h("button", { key: '4521ecf7c406c042b5a3ad79e0f64270217376b0', type: "button", class: `${baseClass}__clear`, onClick: handleClear, tabIndex: -1 }, h("ux-icon", { key: '4cac7650a5f05f9b41a824f6f47255ebbca70537', name: "cross", weight: "normal" }))), hasUnitText && (h("div", { key: '2a406754993967a8b8ddfe075da357dacea28d8f', class: `${baseClass}__unit`, onClick: () => this.setFocus() }, unit)), hasIcon && (h("div", { key: '6130eddc3286c889d52328ddbe9e689a79445d8c', class: `${baseClass}__icon` }, h("ux-icon", { key: 'cc797081a78ae7dc775ca99249255adee1cad04d', name: iconName }))), copyToClipboard && (h("div", { key: 'bf442532d4db7247e88ce63fed9ffb22e9c650b6', class: `${baseClass}__icon` }, h("ux-copy-to-clipboard", { key: '9e31fa262828e5bc1bfdee351c58e8ca73367cfd', "hide-text-to-copy": true, "text-to-copy": this.value || "", "click-to-copy-icon": "copy" })))), h("div", { key: '60a65019462010b83822b6936a0c83814dab9582', class: {
                [`${baseClass}__buttons`]: hasButtons,
                [`${baseClass}--hidden`]: !hasButtons
            } }, h("slot", { key: '833b19457046e74a22860f1176db3bf786681a00', name: "buttons" }))), h("div", { key: 'acb1541a2a01c077360ea37d845762499fbedcc3', id: hintId, class: {
                [`${baseClass}__hint`]: hasHint,
                [`${baseClass}--hidden`]: !hasHint
            } }, hasHintText && h("div", { key: 'd9704c2c90399da941d4010d532282fab4213e3f', class: `${baseClass}__hint-text` }, hintText), h("div", { key: '514ccd6e9241a1907ad874eae3847a425bb41d44', class: {
                [`${baseClass}__hint-slot`]: hasHintSlot,
                [`${baseClass}--hidden`]: !hasHintSlot
            } }, h("slot", { key: 'f7a799047f85a723e7a4130b325deb6ed37d1d2e', name: "hint" }))), h("slot", { key: '4a947b8c633be1ffb5900fa6c1d476d8491bc5de', name: "other" })));
    }
    static get is() { return "ux-input-field"; }
    static get originalStyleUrls() {
        return {
            "$": ["input-field.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["input-field.css"]
        };
    }
    static get properties() {
        return {
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Label text"
                },
                "attribute": "label",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Value of the input element"
                },
                "attribute": "value",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "inputId": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The id attribute of the input element"
                },
                "attribute": "input-id",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"email\" | \"number\" | \"password\" | \"search\" | \"text\" | \"url\"",
                    "resolved": "\"email\" | \"number\" | \"password\" | \"search\" | \"text\" | \"url\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The type attribute of the input element"
                },
                "attribute": "type",
                "reflect": false,
                "defaultValue": "\"text\""
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The name attribute of the input element"
                },
                "attribute": "name",
                "reflect": false
            },
            "labelInfo": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "additional info label for the the input field"
                },
                "attribute": "label-info",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "placeholder for the input element"
                },
                "attribute": "placeholder",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates if the input is disabled"
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "required": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates if the input is required"
                },
                "attribute": "required",
                "reflect": false,
                "defaultValue": "false"
            },
            "error": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates if the input value is invalid"
                },
                "attribute": "error",
                "reflect": false,
                "defaultValue": "false"
            },
            "errorMsg": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Optional error message that is rendered if `error` is set to `true`"
                },
                "attribute": "error-msg",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "errorIcon": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Name of the icon that should be displayed in error state"
                },
                "attribute": "error-icon",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "hintText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Additional hint text shown below the input"
                },
                "attribute": "hint-text",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "clearable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If set to `true` a clear button is rendered that resets the `value` on click"
                },
                "attribute": "clearable",
                "reflect": false,
                "defaultValue": "false"
            },
            "unit": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | null",
                    "resolved": "null | string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "An optional unit is shown besides the input"
                },
                "attribute": "unit",
                "reflect": false,
                "defaultValue": "null"
            },
            "icon": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | null",
                    "resolved": "null | string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Name of the icon that is displayed inside of the field"
                },
                "attribute": "icon",
                "reflect": false,
                "defaultValue": "null"
            },
            "inputmode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"none\" | \"text\" | \"decimal\" | \"numeric\" | \"tel\" | \"search\" | \"email\" | \"url\"",
                    "resolved": "\"decimal\" | \"email\" | \"none\" | \"numeric\" | \"search\" | \"tel\" | \"text\" | \"url\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The inputmode attribute of the input can indicate the browser which keyboard to display."
                },
                "attribute": "inputmode",
                "reflect": false
            },
            "autocomplete": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "autocomplete attribute, will be set on the input element. Refer to HTML standards for allowed values"
                },
                "attribute": "autocomplete",
                "reflect": false,
                "defaultValue": "\"off\""
            },
            "copyToClipboard": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the input field will present a copy to clipboard functionality"
                },
                "attribute": "copy-to-clipboard",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "hasFocus": {}
        };
    }
    static get events() {
        return [{
                "method": "uxInput",
                "name": "uxInput",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when a keyboard input occurred."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "uxChange",
                "name": "uxChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the value has changed."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "uxBlur",
                "name": "uxBlur",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the input loses focus."
                },
                "complexType": {
                    "original": "FocusEvent",
                    "resolved": "FocusEvent",
                    "references": {
                        "FocusEvent": {
                            "location": "global",
                            "id": "global::FocusEvent"
                        }
                    }
                }
            }, {
                "method": "uxFocus",
                "name": "uxFocus",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the input has focus."
                },
                "complexType": {
                    "original": "FocusEvent",
                    "resolved": "FocusEvent",
                    "references": {
                        "FocusEvent": {
                            "location": "global",
                            "id": "global::FocusEvent"
                        }
                    }
                }
            }, {
                "method": "uxInvalid",
                "name": "uxInvalid",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the input is invalid."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "inputChange",
                "name": "uxInputChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "inputChange Event"
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "getInputElement": {
                "complexType": {
                    "signature": "() => Promise<HTMLInputElement>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLInputElement": {
                            "location": "global",
                            "id": "global::HTMLInputElement"
                        }
                    },
                    "return": "Promise<HTMLInputElement>"
                },
                "docs": {
                    "text": "Returns the native input element",
                    "tags": []
                }
            },
            "setFocus": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Set the focus on the input element",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "domNode"; }
    static get watchers() {
        return [{
                "propName": "inputId",
                "methodName": "setInputId"
            }];
    }
}
//# sourceMappingURL=input-field.js.map
